import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  Radio,
  MapPin,
  Coins,
  Gift,
  Swords,
  Crown,
  BadgeCheck,
  Sparkles,
  Users,
  MessageSquare,
  BarChart3,
  Ticket,
  Bell,
  Hand,
  Bookmark,
  ChevronRight,
  Rocket,
  ShieldCheck,
  Compass,
  Rainbow,
  Clock,
  ImageIcon,
  Eye,
  HelpCircle,
  Users2,
  Award,
} from "lucide-react";

const SECTIONS = [
  { id: "welcome",       label: "Welcome",              Icon: Rocket },
  { id: "side-picker",   label: "Two Sides",            Icon: Rainbow },
  { id: "trial",         label: "Your 30-Day Trial",    Icon: Clock },
  { id: "feed",          label: "Feed & Composer",      Icon: Home },
  { id: "coins",         label: "Coins & CoinStore",    Icon: Coins },
  { id: "top-badge",     label: "Top Badge Program",    Icon: Award },
  { id: "going-live",    label: "Going Live",           Icon: Radio },
  { id: "watching-live", label: "Watching Live",        Icon: Gift },
  { id: "cohost",        label: "Co-hosting",           Icon: Users },
  { id: "communities",   label: "Communities & Chat",   Icon: MessageSquare },
  { id: "nearby",        label: "Social Meet (Map)",    Icon: MapPin },
  { id: "battles",       label: "Battles + Champion",   Icon: Swords },
  { id: "golden-ticket", label: "Sunday Golden Ticket", Icon: Ticket },
  { id: "ambassador",    label: "Ambassador Program",   Icon: Users2 },
  { id: "notifications", label: "Notification Center",  Icon: Bell },
  { id: "privacy",       label: "Privacy & @handle",    Icon: Eye },
  { id: "creator",       label: "Creator Dashboard",    Icon: BarChart3 },
  { id: "faq",           label: "FAQ pages",            Icon: HelpCircle },
  { id: "safety",        label: "Safety",               Icon: ShieldCheck },
];

function Chip({ children }) {
  return (
    <span className="inline-block text-[10px] uppercase tracking-widest border border-white/10 bg-white/5 text-zinc-300 rounded-full px-2 py-0.5 mr-1.5 mb-1.5">
      {children}
    </span>
  );
}

function Section({ id, label, Icon, children }) {
  return (
    <section id={id} className="scroll-mt-24 py-8 border-b border-white/5" data-testid={`tutorial-section-${id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-rose-600/20 border border-rose-600/40 flex items-center justify-center text-rose-400">
          <Icon size={20} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            My Panda X · tutorial
          </div>
          <h2 className="font-display text-3xl text-white">{label}</h2>
        </div>
      </div>
      <div className="text-sm text-zinc-300 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function Cta({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-rose-400 hover:text-rose-300 font-semibold text-sm"
    >
      {children}
      <ChevronRight size={14} />
    </Link>
  );
}

export default function TutorialPage() {
  const [active, setActive] = useState("welcome");

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id);
        }
      },
      { rootMargin: "-30% 0px -55% 0px" }
    );
    document.querySelectorAll("section[id]").forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-0 pb-24" data-testid="tutorial-page">
      {/* Hero */}
      <div className="pt-8 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-rose-500 mb-2">Getting started</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">Welcome to Panda&nbsp;X.</h1>
        <p className="text-zinc-400 max-w-2xl leading-relaxed">
          Everything you can do here — Feed, Live streams, Social Meet, Battles, Gifts, Golden Tickets —
          explained in one place. Read top-to-bottom, or jump to the section you need.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Sticky in-page nav */}
        <nav
          className="hidden lg:block sticky top-24 self-start"
          aria-label="Tutorial sections"
          data-testid="tutorial-toc"
        >
          <ul className="space-y-1">
            {SECTIONS.map(({ id, label, Icon }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  data-testid={`tutorial-toc-${id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition ${
                    active === id
                      ? "bg-rose-600/15 text-rose-300 border border-rose-600/30"
                      : "text-zinc-500 hover:text-white"
                  }`}
                >
                  <Icon size={14} /> {label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-6 border-t border-white/5 text-[11px] text-zinc-500 leading-relaxed">
            Tip: you can revisit this at any time from the profile menu → <span className="text-white">Help & Tutorial</span>.
          </div>
        </nav>

        <div>
          <Section id="welcome" label="Welcome" Icon={Rocket}>
            <p>
              My Panda X is an <b>18+ social entertainment platform</b>: go live, join private
              communities, meet people on the map and send virtual gifts. Free to join with a <b>30-day trial of Standard</b>.
            </p>
            <div className="flex flex-wrap">
              <Chip>Two sides · LGBTQ / Straight</Chip>
              <Chip>30-day free trial</Chip>
              <Chip>Live streaming + co-host</Chip>
              <Chip>Private communities</Chip>
              <Chip>Virtual gifts (55/45)</Chip>
              <Chip>Ambassador commissions</Chip>
              <Chip>Social Meet map</Chip>
              <Chip>Sunday Golden Ticket 🎟️</Chip>
              <Chip>Battles & champions</Chip>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              <Cta to="/feed" testId="tut-cta-feed">Open the Feed</Cta>
              <Cta to="/live" testId="tut-cta-live">Watch someone live</Cta>
              <Cta to="/nearby" testId="tut-cta-nearby">Open Social Meet</Cta>
            </div>
          </Section>

          <Section id="side-picker" label="Two Sides · Pick Yours" Icon={Rainbow}>
            <p>
              My Panda X runs as <b>two separate audiences</b>: the <span className="text-rose-400 font-medium">LGBTQ side</span> and the <span className="text-sky-300 font-medium">Straight side</span>. Feeds, live streams, communities, Social Meet, and recommendations <b>never mix across sides</b>.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>First-time login? You'll see a mandatory picker before you can browse the feed.</li>
              <li>The <b>side pill</b> in your left rail (desktop) or top header (mobile) shows which side you're on and lets you switch anytime.</li>
              <li>When you switch, all your existing posts, streams, communities, and reels are <b>retroactively moved</b> to the new side.</li>
              <li>Public visitors on the marketing landing page can also preview each side before signing up.</li>
            </ul>
          </Section>

          <Section id="trial" label="Your 30-Day Free Trial" Icon={Clock}>
            <p>
              Every new member starts with a <b>30-day free trial of the Standard membership</b>. No card required, no auto-renew, no charge when it ends.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>See how many days are left in the <b>amber Trial pill</b> in the left rail.</li>
              <li>When the trial ends, we <b>expire silently</b> — you keep using the app, you just lose Standard-only perks until you subscribe.</li>
              <li>Older accounts received a 100-coin bonus that has since been retired — the trial replaced it as the welcome perk.</li>
            </ul>
          </Section>

          <Section id="feed" label="Feed & Composer" Icon={Home}>
            <p>
              The Feed shows posts from creators you follow, plus a curated pulse of the community.
              Tap the <b>composer bar</b> at the top to write, upload photos, or start a poll.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Heart a post to like it — creators love it and it boosts their reach.</li>
              <li>Tap the <b>gift icon</b> under any post to send a rose, fire, diamond, or up to a yacht.</li>
              <li>Reply, quote, or DM the creator to start a conversation.</li>
              <li>The <b>Battle Champions</b> Hall of Fame strip lives at the top so you can see who's reigning.</li>
            </ul>
          </Section>

          <Section id="coins" label="Coins & CoinStore" Icon={Coins}>
            <p>
              <b>Coins are the fuel of My Panda X</b> — you spend them on gifts, nudges, prize-wheel spins,
              and paid-entry live streams. You earn them when others gift you back.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Buy coin packs in the <b>Coin Store</b> (Stripe checkout, instant credit). Five fixed packs:</li>
            </ul>
            <div className="overflow-x-auto" data-testid="tut-coin-packages">
              <table className="text-xs w-full max-w-md border border-white/10">
                <thead className="text-zinc-500 uppercase tracking-widest text-[10px]"><tr><th className="text-left p-2">Pack</th><th className="text-right p-2">Price</th><th className="text-right p-2">Coins</th></tr></thead>
                <tbody className="text-zinc-200">
                  {[["Starter", "$4.99", "500"], ["Popular", "$14.99", "2,000"], ["Most Popular", "$24.99", "3,700"], ["Best Value", "$99.99", "16,500"], ["Power Pack", "$249.99", "43,000"]].map(([n, p, c]) => (
                    <tr key={n} className="border-t border-white/5"><td className="p-2 text-white">{n}</td><td className="p-2 text-right">{p}</td><td className="p-2 text-right tabular-nums">{c}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Starter pack bonus</b>: new accounts get <b>15% extra coins</b> on the Starter pack (500 → 575). There are no other bonuses — no Friday bonus, no custom amounts.</li>
              <li>New accounts get a <b>30-day free trial</b> instead of the old 100-coin welcome bonus.</li>
              <li>Every purchase makes you a <b>Paying Customer</b>, which unlocks the Sunday Golden Ticket.</li>
              <li><b>Tip revenue split</b>: creators keep <b>55%</b>, platform takes 45%. No hidden fees.</li>
            </ul>
            <Cta to="/coins" testId="tut-cta-coins">Go to the Coin Store</Cta>
          </Section>

          <Section id="top-badge" label="Top Badge Program" Icon={Award}>
            <p>
              The <b>Top Badge</b> is a gold creator award shown beside your name everywhere the VIP badge appears — profile, posts,
              live chat and viewer lists. It is separate from monthly VIP status, and Top Badge holders convert Bamboo at the better 2.1 rate.
            </p>
            <ul className="list-disc pl-5 space-y-1" data-testid="tut-top-badge-rules">
              <li><b>Kickoff launches October 5, 2026</b> at 12:00 AM Eastern on the <Link to="/top-badge" className="text-rose-400 hover:text-rose-300 font-medium">Top Badge campaign page</Link>.</li>
              <li><b>How to apply</b>: anyone can enter — upload a <b>15-second video</b> telling members why you deserve it. Submissions close <b>Thursday, October 8 at 11:59 PM Eastern</b>. One entry per member.</li>
              <li><b>How voting works</b>: every member gets <b>one vote, total</b> — pick carefully, votes are final. You can’t vote for your own entry.</li>
              <li><b>Winners</b>: voting closes <b>Friday, October 9 at 11:59 PM Eastern</b>. The <b>top 5 by votes earn the Top Badge instantly</b> — if there’s a tie for 5th, everyone tied wins. Winners see the badge on their profile right away and a congratulations message on their next login.</li>
              <li><b>Monthly goals (from November 2026)</b>: each calendar month (Eastern), stream <b>25 hours</b> <i>and</i> earn <b>50,000 🎋 from gifts</b>. Right after month-end we review: meet both goals and you earn or keep the Top Badge; miss them and a monthly badge is removed — you re-qualify every month. Track your pace on the Creator Dashboard.</li>
              <li><b>Kickoff winners</b> are exempt from monthly goals through <b>December 31, 2026</b>, then join the monthly review like everyone else.</li>
              <li><b>Rally votes</b>: use <b>Share</b> on your entry to copy a direct link to your video and post it anywhere.</li>
            </ul>
            <Cta to="/top-badge" testId="tut-cta-top-badge">Go to the Top Badge campaign</Cta>
          </Section>

          <Section id="going-live" label="Going Live" Icon={Radio}>
            <p>
              Anyone can go live. Tap the <b>Go Live</b> button in the left rail — you'll see a
              full-screen camera preview with a Fanbase-style setup panel.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><Sparkles size={12} className="inline mb-0.5 text-yellow-400" /> <b>Beauty</b> & <b>Filter</b> — subtle enhancements applied to the preview.</li>
              <li><Coins size={12} className="inline mb-0.5 text-yellow-400" /> <b>Paid Live</b> — charge viewers coins to join.</li>
              <li><b>Goal</b> — set a coin goal; a progress bar shows how close you are.</li>
              <li><b>Wheel</b> — configure prize segments viewers can win (25 coins per spin).</li>
              <li><b>Custom Gifts</b>, <b>Poll</b>, <b>Split view</b>, and <b>Extensions</b> tune the room.</li>
            </ul>
            <p className="text-zinc-500 text-xs">
              Pro tip: viewers keep tipping longer when you set a goal + wheel + one big gift you thank publicly.
            </p>
          </Section>

          <Section id="watching-live" label="Watching Live · Gifts · Combos · Surf" Icon={Gift}>
            <p>
              Tap any card on the <Link to="/live" className="text-rose-400 hover:text-rose-300">Live tab</Link> to enter a stream.
              The view is a <b>full-screen video with transparent overlays</b>:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Chat streams up from the bottom-left with a soft fade.</li>
              <li>Tap the yellow <b>Gift</b> button to send from the animated gift set — <b>Rose, Fire, Diamond, Yacht, Bottle, Cake, Kiss, Castle, and more</b>. Each has a custom SVG burst that plays for every viewer.</li>
              <li><b>Combos</b>: fire the same gift 5× in 6 s and the host sees `×5` on your card.</li>
              <li>Tap the video anywhere for a floating heart, TikTok-style.</li>
              <li>Right-rail <ChevronRight size={12} className="inline mb-0.5" /> jumps you to the <b>next-nearest live stream</b> — or swipe left on mobile.</li>
              <li>Auto-<b>Thank you DM</b> lands in your inbox the first time a creator has that setting on and you gift them.</li>
              <li>Tap the host chip in the top-left to enlarge their profile picture and hit <b>🔔 Notify me when they go live</b> for an in-app ping next time.</li>
            </ul>
          </Section>

          <Section id="cohost" label="Co-hosting · Invite or Request" Icon={Users}>
            <p>
              Streams can go from solo to <b>multi-guest</b> in a tap. Two ways in:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Host invites you</b>: an <b>Accept / Decline banner</b> appears at the top of the live room. Accept, and you go live as a co-host on the same stream.</li>
              <li><b>You request to join</b>: tap <b>Raise your hand 🖐️</b> from the action rail — the host sees your request in a side panel and approves.</li>
              <li>All co-host controls (mute, remove) live on the host's side.</li>
            </ul>
          </Section>

          <Section id="communities" label="Communities & Chat" Icon={MessageSquare}>
            <p>
              <b>Communities</b> are private + public rooms with a shared feed, real-time chat, and roles. Great for VIPs, subscribers, or friend groups.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Discover from the Communities tab (side-filtered) — some are open, others are <b>request to join</b>.</li>
              <li>Community chat supports <b>drag-and-drop images</b> — drop a photo into the composer and it uploads instantly to secure storage.</li>
              <li>Owners get moderation tools: pin posts, remove members, restrict who can post.</li>
              <li>Every community is stamped with your <b>side</b> at creation — so it only appears to viewers on the same side.</li>
            </ul>
          </Section>

          <Section id="nearby" label="Social Meet (Sniffies-style Map)" Icon={MapPin}>
            <p>
              Social Meet is a full-screen dark map showing every user around you as an
              avatar-pin. Live hosts pulse red, online users glow emerald, verified creators wear a
              gold dot. Locations are <b>jittered ±0.15 mi</b> so exact addresses stay private.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><Compass size={12} className="inline mb-0.5" /> Toggle <b>Live only</b> to hide everyone who isn't streaming — the map auto-flies to the closest live host.</li>
              <li><Bookmark size={12} className="inline mb-0.5" /> Save your favourite <b>radius + filters</b> combos as named searches for one-tap recall.</li>
              <li>Tap any pin (or a card in the bottom strip) to open the <b>profile preview</b>.</li>
              <li><Hand size={12} className="inline mb-0.5 text-yellow-400" /> Send a <b>Nudge · 1 coin</b> — a lightweight flirty ping that lands as a notification. Throttled 5 min per person.</li>
            </ul>
            <Cta to="/nearby" testId="tut-cta-nearby-2">Open Social Meet</Cta>
          </Section>

          <Section id="battles" label="Battles + Battle Champion" Icon={Swords}>
            <p>
              Two hosts can duel head-to-head: whoever collects more coin-value gifts inside the
              battle window <b>wins</b>. Battles run on the live viewer with a timer + score bar.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Start a battle from your live room's right-rail <Swords size={12} className="inline mb-0.5 text-rose-400" /> button — pick the opponent's stream.</li>
              <li>Your profile keeps a full <b>battle history</b>: wins, losses, win rate, coins earned.</li>
              <li>Every month the <b>#1 battler</b> is crowned <Crown size={12} className="inline mb-0.5 text-yellow-400" /> <b>Battle Champion</b> — <b>+500 coins</b> + a gold-crown badge for 30 days.</li>
              <li>Past champions appear on the <b>Hall of Fame</b> strip on the Feed.</li>
            </ul>
          </Section>

          <Section id="golden-ticket" label="Sunday Golden Scratch Ticket" Icon={Ticket}>
            <p>
              Every <b>Sunday (UTC)</b>, paying members get one <b>Golden Scratch Ticket</b>. One tap to reveal your prize — coins, rare gifts, or a boost. Hidden from the UI the other six days of the week.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Available only on Sundays — the ticket icon appears in your nav.</li>
              <li>Requires at least one coin purchase (Paying Customer status).</li>
              <li>Prizes range from a handful of coins up to a <b>5,000-coin jackpot</b>.</li>
              <li>Come back next Sunday for a fresh spin — one per account per week.</li>
            </ul>
          </Section>

          <Section id="ambassador" label="Ambassador Program · Refer & Earn" Icon={Users2}>
            <p>
              Panda Ambassadors are the loyalists helping grow the pack. Apply once — get a golden
              badge, <b>500 coins/month</b>, boosted reach, and <b>real USD commissions</b> on the
              creators you refer.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Only tips + creator-shop purchases qualify</b>. Coin gifts and live gifts don't count (they'd double-count).</li>
              <li>Commission is carved from the <b>creator's 55% share</b>, never the platform's 45%. Current rates are shown to approved Ambassadors in their dashboard and step down over each creator's first months.</li>
              <li>The clock starts on <b>each performer's first successful paid transaction</b>, not signup — so slow-starters still bring you full-rate months.</li>
              <li>Attribution is <b>one-shot at signup</b> — whoever's referral link they used is their ambassador for life.</li>
              <li>Refunds and chargebacks <b>automatically reverse</b> the matching commission. Earnings sit for <b>30 days</b> in a dispute-safe holding window before becoming withdrawable.</li>
              <li>Payout via <b>Stripe Connect</b>, <b>$200 minimum</b> — the same floor as a Bamboo cash-out (80,000 🎋 = $200) — on-demand from your Ambassador dashboard.</li>
              <li>Track everything from <Link to="/ambassador/dashboard" className="text-amber-300 hover:text-amber-200 font-medium">/ambassador/dashboard</Link> — lifetime earnings, held vs withdrawable, per-transaction breakdown.</li>
            </ul>
            <div className="flex flex-wrap gap-4 pt-2">
              <Cta to="/ambassadors/apply" testId="tut-cta-ambassador-apply">Apply to become an Ambassador</Cta>
              <Cta to="/ambassadors" testId="tut-cta-ambassador-leaderboard">See the leaderboard</Cta>
            </div>
          </Section>

          <Section id="notifications" label="Notification Center" Icon={Bell}>
            <p>
              A paginated feed of everything happening around you — new followers, tips, gifts, battle invites, co-host invites, community activity, and broadcast announcements.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Rich cards for broadcasts, wheel prizes, and creator gifts.</li>
              <li>Actionable rows: <b>Follow back</b>, <b>Join live 🔴</b>, or open the relevant page directly from the notification.</li>
              <li><b>🔔 Live Follow Bell</b>: tap someone's profile picture → hit "Notify me when @creator goes live" and you'll get a `live_started` notification the moment they hit Go Live.</li>
              <li>Mark all read from the top of the page.</li>
            </ul>
          </Section>

          <Section id="privacy" label="Privacy · @handle · Profile Peek" Icon={Eye}>
            <p>
              Fine-grained visibility controls for your identity and profile.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Show my @username publicly</b> toggle (Profile Edit): when off, other members only see your display name on posts, live streams, DMs, and your profile. Your profile URL still works, and admins always see it.</li>
              <li><b>Profile peek</b>: tapping any user's avatar OR display name — on post cards, the live host chip, admin user rows, or a DM header — opens a full-screen enlarged view of their profile picture. No page navigation required.</li>
              <li><b>Ghost mode</b> on Social Meet: hide yourself from the map without leaving.</li>
              <li>Age gate on landing — cookie-remembered so you don't see it again.</li>
            </ul>
          </Section>

          <Section id="creator" label="Creator Dashboard" Icon={BarChart3}>
            <p>
              If you have Creator status, the <Link to="/creator" className="text-rose-400 hover:text-rose-300">Creator Dashboard</Link>
              becomes your operations center.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Analytics</b> — coins earned today/week/month, top posts, top fans, gift breakdown, peer percentile.</li>
              <li><b>Inbox</b> — DMs sorted by top gifter / unread / VIPs, with mark-all-read.</li>
              <li><b>Prizes</b> — wheel spin winners land here as actionable tasks; mark them done with an optional note that auto-DMs the winner.</li>
              <li><b>Templates</b> — save canned replies + toggle an <b>auto-reply</b> for first-time DMs or live gifts (`{"{sender}"}`, `{"{gift}"}`, `{"{coins}"}` variables).</li>
              <li><b>Broadcast scheduling</b> with <b>audience segments</b>: All, Creators, Ambassadors, LGBTQ side, Straight side — hit the exact subset of your audience.</li>
              <li><b>Bamboo</b> — see your 🎋 balance, convert to coins, send Bamboo, request a cash-out (400 🎋 = $1, minimum 80,000 🎋) and review your private activity history.</li>
              <li><b>Payouts</b> — Stripe Express Connect, standard 2-business-day payout. Tip revenue split: <b>you keep 55%</b>, platform 45%.</li>
            </ul>
          </Section>

          <Section id="faq" label="FAQ · Creators & Members" Icon={HelpCircle}>
            <p>
              Two public FAQ pages spell out every policy in plain English — no lawyerese.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><Link to="/faq/creators" className="text-rose-400 hover:text-rose-300 font-medium">/faq/creators</Link> — payout split, Stripe onboarding, subscription revenue rules, content rules, Sunday Golden Ticket for creators.</li>
              <li><Link to="/faq/members" className="text-rose-400 hover:text-rose-300 font-medium">/faq/members</Link> — 30-day trial mechanics, silent expiration, coins, tip transparency, 18+ policy.</li>
              <li>Not covered? DM <b>@panda</b> from your inbox — response within one business day.</li>
            </ul>
          </Section>

          <Section id="safety" label="Safety & Trust" Icon={ShieldCheck}>
            <p>
              My Panda X is 18+. You'll see the age gate on your first visit — checking it saves a
              cookie so you don't see it again.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Report inappropriate posts, DMs, or streams from the ••• menu — mods review the queue.</li>
              <li>Block a user from their profile if you want them out of your feed and inbox permanently.</li>
              <li>Your exact location is never shown to others — pins are jittered daily.</li>
              <li>You can request account deletion at any time from settings.</li>
            </ul>
          </Section>

          <div className="pt-10 flex flex-col items-start gap-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">All caught up</div>
            <h3 className="font-display text-2xl">Ready to try it?</h3>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/feed"
                data-testid="tut-end-feed"
                className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-5 py-2.5 rounded-full text-sm"
              >
                <Home size={14} className="inline mr-1.5" /> Open the Feed
              </Link>
              <Link
                to="/live"
                data-testid="tut-end-live"
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-semibold px-5 py-2.5 rounded-full text-sm"
              >
                <Radio size={14} className="inline mr-1.5" /> See who's live
              </Link>
              <Link
                to="/nearby"
                data-testid="tut-end-nearby"
                className="border border-white/15 hover:bg-white/5 text-white font-semibold px-5 py-2.5 rounded-full text-sm"
              >
                <MapPin size={14} className="inline mr-1.5" /> Explore Social Meet
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
